import React from 'react';
import FieldReportHeader from './FieldReportHeader';
import { MapPin, Camera, Clock, CheckCircle, XCircle, Ticket, Calendar } from 'lucide-react';
import { resolveFileUrl, resolveSignatureUrl } from '../../lib/fileUtils';
import { SafeImage } from '../SafeImage';
import { displayValue } from '../../lib/displayValue';

// Helper to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'bg-green-100 text-green-700';
    case 'submitted_pending_review': return 'bg-blue-100 text-blue-700';
    case 'draft': return 'bg-slate-100 text-slate-700';
    default: return 'bg-slate-100 text-slate-700';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'completed': return 'Completed';
    case 'submitted_pending_review': return 'Pending Review';
    case 'draft': return 'Draft';
    default: return status || 'Draft';
  }
};

// Utility color mapping for visual indicator
const getUtilityColor = (colorName) => {
  const colors = {
    'Red': 'bg-red-500',
    'Yellow': 'bg-yellow-400',
    'Orange': 'bg-orange-500',
    'Blue': 'bg-blue-500',
    'Green': 'bg-green-500',
    'Purple': 'bg-purple-500',
    'Pink': 'bg-pink-500',
    'White': 'bg-white border border-slate-300',
  };
  return colors[colorName] || 'bg-slate-300';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    // Avoid the "new Date('YYYY-MM-DD')" UTC trap that flips date-only strings
    // back one day in negative timezones (was rendering May 19 locates as May 18 EST).
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr).trim());
    const date = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true 
    });
  } catch {
    return dateStr;
  }
};

export default function LocateDocument({ report, printRef }) {
  return (
    <div 
      ref={printRef} 
      id="locate-document" 
      className="bg-white w-full max-w-4xl shadow-xl rounded-lg overflow-hidden print:shadow-none print:rounded-none print:max-w-none" 
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <FieldReportHeader reportType="Locate Report" reportNumber={report.ticket_number || report.job_number} />
      
      <div className="px-6 py-4">
        {/* Title & Status */}
        <div className="mb-4 text-center">
          <h2 className="text-lg font-semibold text-slate-900">Utility Locate Report</h2>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(report.status)}`}>
              {getStatusLabel(report.status)}
            </span>
            {(Array.isArray(report.locate_type) ? report.locate_type.length > 0 : !!report.locate_type) && (
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                {displayValue(report.locate_type)}
              </span>
            )}
          </div>
        </div>

        {/* Job & Site Info */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Job Information</p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-[10px]">Job Number</p>
              <p className="font-medium text-amber-600">{report.job_number || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Locate Date</p>
              <p className="font-medium text-slate-700">{formatDate(report.locate_date)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Technician</p>
              <p className="font-medium text-slate-700">{report.locate_technician || '—'}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-slate-500 text-[10px]">Site Location</p>
              <p className="font-medium text-slate-700 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-slate-400" />
                {report.site_location || '—'}
              </p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px] flex items-center gap-1">
                <Ticket className="w-3 h-3" /> Ticket Number
              </p>
              <p className="font-medium text-amber-600 text-lg">{report.ticket_number || '—'}</p>
            </div>
          </div>
        </div>

        {/* Project Info */}
        <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-100">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-600 mb-2">Project Details</p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-blue-500 text-[10px]">Project Name</p>
              <p className="font-medium text-slate-700">{report.project_name || '—'}</p>
            </div>
            <div>
              <p className="text-blue-500 text-[10px]">Client/Requestor</p>
              <p className="font-medium text-slate-700">{report.client_requestor || '—'}</p>
            </div>
            <div>
              <p className="text-blue-500 text-[10px] flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Valid Until
              </p>
              <p className="font-medium text-slate-700">{formatDate(report.valid_until)}</p>
            </div>
          </div>
        </div>

        {/* Utilities Located */}
        {report.utilities && report.utilities.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Utilities Located ({report.utilities.length})</p>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">#</th>
                  <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Found</th>
                  <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Not Found</th>
                  <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Owner</th>
                  <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Color</th>
                  <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Method(s)</th>
                </tr>
              </thead>
              <tbody>
                {report.utilities.map((utility, idx) => (
                  <React.Fragment key={idx}>
                    <tr className={utility.notes ? 'border-t border-slate-100' : 'border-b border-slate-100'}>
                      <td className="py-1.5 px-2 text-slate-500 text-[11px]">{idx + 1}</td>
                      <td className="py-1.5 px-2 text-slate-700 text-[11px] font-medium">{displayValue(utility.utility_type)}</td>
                      <td className="py-1.5 px-2 text-slate-700 text-[11px]">{displayValue(utility.utility_not_found)}</td>
                      <td className="py-1.5 px-2 text-slate-700 text-[11px]">{utility.utility_owner || '—'}</td>
                      <td className="py-1.5 px-2 text-slate-700 text-[11px]">
                        {(() => {
                          const colors = Array.isArray(utility.marked_color)
                            ? utility.marked_color
                            : (utility.marked_color ? [utility.marked_color] : []);
                          if (colors.length === 0) return '—';
                          return (
                            <div className="flex items-center gap-1 flex-wrap">
                              {colors.map((c, i) => (
                                <span key={i} className="inline-flex items-center gap-1">
                                  <span className={`w-3 h-3 rounded-full ${getUtilityColor(c)}`}></span>
                                  {c}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-1.5 px-2 text-slate-700 text-[11px]">
                        {Array.isArray(utility.marking_methods)
                          ? utility.marking_methods.join(', ')
                          : (utility.marking_method || '—')}
                      </td>
                    </tr>
                    {utility.notes && (
                      <tr className="border-b border-slate-100">
                        <td></td>
                        <td colSpan={5} className="pb-2 pt-0 px-2">
                          <div className="text-[10px] text-slate-500 italic">
                            <span className="font-semibold not-italic">Notes:</span> {utility.notes}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Site Conditions */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Site Conditions</p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-[10px]">Markings Placed</p>
              <p className="font-medium text-slate-700">{displayValue(report.markings_placed)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Site Conditions</p>
              <p className="font-medium text-slate-700">{displayValue(report.site_conditions)}</p>
            </div>
          </div>
        </div>

        {/* Photos Section */}
        <div className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
            <Camera className="w-3 h-3" /> Documentation Photos {report.locate_photos?.length ? `(${report.locate_photos.length})` : ''}
          </p>
          {/* Cover photo (Section E) + supplementary photos render in the
              SAME 2-column grid so every shot on the PDF is half-page
              width — the user explicitly wanted the cover image to match
              the standard gallery size rather than dominate the page. The
              cover is first; a "Cover" label distinguishes it. */}
          {(report.locate_image?.url || (report.locate_photos && report.locate_photos.length > 0)) ? (
            <div className="grid grid-cols-2 gap-4">
              {report.locate_image?.url && (
                <div className="break-inside-avoid">
                  <SafeImage
                    src={resolveFileUrl(report.locate_image, 'preview')}
                    alt={report.locate_image.caption || 'Locate cover image'}
                    className="w-full h-72 object-cover rounded border border-slate-200"
                    crossOrigin="anonymous"
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words">
                    <span className="text-[10px] uppercase tracking-wide text-amber-600 font-semibold mr-1">Cover</span>
                    {report.locate_image.caption || ''}
                  </p>
                </div>
              )}
              {(report.locate_photos || []).map((photo, idx) => (
                <div key={idx} className="break-inside-avoid">
                  <SafeImage
                    src={resolveFileUrl(photo, 'preview')}
                    alt={photo.caption || `Locate Photo ${idx + 1}`}
                    className="w-full h-72 object-cover rounded border"
                    loading="lazy"
                    decoding="async"
                  />
                  {photo.caption && (
                    <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words">{photo.caption}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">No photos attached</p>
          )}
        </div>

        {/* Notes */}
        {report.general_notes && (
          <div className="mb-4 p-3 bg-slate-50 rounded">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">General Notes</p>
            <p className="text-sm text-slate-600">{report.general_notes}</p>
          </div>
        )}

        {/* Signatures Section */}
        <div className="mb-4 border-t border-slate-200 pt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Signatures & Authorization</p>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Submitter Signature */}
            <div className="p-3 bg-slate-50 rounded">
              <p className="text-[10px] font-semibold text-slate-500 mb-2">Submitter / Technician</p>
              {report.submitter_signature_url ? (
                <div>
                  <img 
                    src={resolveSignatureUrl(report, 'submitter_signature')} 
                    alt="Submitter Signature" 
                    className="max-h-24 mb-2 border border-slate-200 rounded bg-white p-1" 
                  />
                  <p className="text-sm font-medium text-slate-700">{report.submitter_name || report.submitted_by || '—'}</p>
                  <p className="text-[10px] text-slate-500">{formatDateTime(report.submitter_signed_at)}</p>
                </div>
              ) : (
                <div>
                  <div className="border-b-2 border-slate-300 h-12 mb-1 flex items-end">
                    <span className="text-slate-300 text-sm italic pb-1">Not signed</span>
                  </div>
                  <p className="text-[10px] text-slate-500">Technician Signature & Date</p>
                </div>
              )}
            </div>

            {/* Reviewer/Approver Signature */}
            <div className={`p-3 rounded ${report.status === 'completed' ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}>
              <p className={`text-[10px] font-semibold mb-2 ${report.status === 'completed' ? 'text-green-600' : 'text-slate-500'}`}>
                Manager Approval
              </p>
              {report.reviewer_signature_url ? (
                <div>
                  <img 
                    src={resolveSignatureUrl(report, 'reviewer_signature')} 
                    alt="Reviewer Signature" 
                    className="max-h-24 mb-2 border border-green-200 rounded bg-white p-1" 
                  />
                  <p className="text-sm font-medium text-green-700">{report.reviewer_name || report.reviewed_by || '—'}</p>
                  <p className="text-[10px] text-green-600">{formatDateTime(report.reviewer_signed_at || report.reviewed_at)}</p>
                </div>
              ) : (
                <div>
                  <div className="border-b-2 border-slate-300 h-12 mb-1 flex items-end">
                    <span className="text-slate-300 text-sm italic pb-1">
                      {report.status === 'submitted_pending_review' ? 'Awaiting approval' : 'Not signed'}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">Manager Signature & Date</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status History */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Report Status</p>
          <div className="flex flex-wrap gap-4 text-[11px]">
            {report.created_at && (
              <div className="flex items-center gap-1 text-slate-600">
                <Clock className="w-3 h-3" />
                <span>Created: {formatDateTime(report.created_at)}</span>
              </div>
            )}
            {report.submitted_at && (
              <div className="flex items-center gap-1 text-blue-600">
                <CheckCircle className="w-3 h-3" />
                <span>Submitted: {formatDateTime(report.submitted_at)}</span>
              </div>
            )}
            {report.reviewed_at && report.status === 'completed' && (
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle className="w-3 h-3" />
                <span>Approved: {formatDateTime(report.reviewed_at)}</span>
              </div>
            )}
            {report.rejected_at && (
              <div className="flex items-center gap-1 text-red-600">
                <XCircle className="w-3 h-3" />
                <span>Rejected: {formatDateTime(report.rejected_at)}</span>
              </div>
            )}
          </div>
          {report.rejection_reason && (
            <div className="mt-2 p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700">
              <span className="font-semibold">Rejection Reason:</span> {report.rejection_reason}
            </div>
          )}
        </div>
      </div>
      
      {/* Legal Disclaimer */}
      <div className="px-6 py-3 bg-slate-100 border-t border-slate-200">
        <p className="text-[9px] text-slate-500 text-center italic">
          This report documents field observations at the time of service. 
          Utility locations are approximate unless physically exposed. Call 811 before you dig.
        </p>
      </div>
      
      {/* Footer bar */}
      <div className="px-6 py-3 bg-slate-800 flex items-center justify-between text-[10px] text-slate-300">
        <span>Center Line Locating, LLC · Charlotte, NC · (704) 315-5820</span>
        <span>Locate Report #{report.ticket_number || report.job_number}</span>
      </div>
    </div>
  );
}
